/**
 * User API
 */

import { apiFetch } from './config';
import type { User, Client, UserEntitlement, UsageStats } from '../types';

export interface CompanyMember {
  email: string;
  display_name?: string | null;
  role: 'admin' | 'member';
  status?: string;
  billing_type?: 'self_paid' | 'admin_paid';
  created_at: string | null;
  last_active?: string | null;
  company_id: string;
  client_id: string;
}

export const userApi = {
  /**
   * Get user profile
   */
  getUser: async (email: string) => {
    return apiFetch<User>(`/api/users/me`);
  },

  /**
   * Get user entitlements (company access)
   */
  getEntitlements: async (email: string) => {
    return apiFetch<UserEntitlement[]>(`/api/users/entitlements`);
  },

  /**
   * List users for a company
   */
  listUsers: async (companyId: string) => {
    const response = await apiFetch<any>(`/api/company/users?company_id=${encodeURIComponent(companyId)}`);
    if (!response.success) return { success: false, error: response.error };
    const body = response.data?.data || response.data;
    const records = Array.isArray(body) ? body : body?.Records;
    if (!Array.isArray(records)) return { success: false, error: 'Invalid team member response' };
    return { success: true, data: records as CompanyMember[] };
  },

  /**
   * Invite user to company
   */
  inviteUser: async (companyId: string, email: string, role: string, billing: 'self_pays' | 'admin_pays' = 'self_pays') => {
    return apiFetch<{ message: string }>(`/api/company/users`, {
      method: 'POST',
      body: JSON.stringify({ company_id: companyId, email, role, billing }),
    });
  },

  /**
   * Update user role
   */
  updateUserRole: async (companyId: string, email: string, role: string) => {
    return apiFetch<{ message: string }>(`/api/company/users`, {
      method: 'PUT',
      body: JSON.stringify({ company_id: companyId, email, role }),
    });
  },

  /**
   * Remove user from company
   */
  removeUser: async (companyId: string, email: string) => {
    return apiFetch<{ message: string }>(`/api/company/users?company_id=${encodeURIComponent(companyId)}&email=${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  },

  /**
   * Update user profile (display name)
   */
  updateProfile: async (data: { first_name?: string; last_name?: string }) => {
    return apiFetch<{ email_address: string; first_name: string; last_name: string; display_name: string }>('/api/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get weekly splash screen data
   */
  getSplash: async () => {
    return apiFetch<any>('/api/user/splash');
  },

  /**
   * Acknowledge weekly splash screen
   */
  acknowledgeSplash: async (weekStart: string) => {
    return apiFetch<any>('/api/user/splash/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ week_start: weekStart }),
    });
  },
};
